import { createContext, useContext, useEffect, useLayoutEffect, useState, type ReactNode } from 'react';
import { clearLeaf } from './page-title';

const LeafValue = createContext<string | null>(null);
const LeafSetter = createContext<LeafUpdate | null>(null);

/** Holds the page's own title (its last breadcrumb), which `AppLayout` adds in front of the nav entry's labels in `document.title`. */
export function PageTitleLeafProvider({ children }: { children: ReactNode }) {
  const [leaf, setLeaf] = useState<string | null>(null);
  return (
    <LeafSetter.Provider value={setLeaf}>
      <LeafValue.Provider value={leaf}>{children}</LeafValue.Provider>
    </LeafSetter.Provider>
  );
}

/** What the mounted page contributed, or null. */
export function usePageTitleLeafValue(): string | null {
  return useContext(LeafValue);
}

type LeafUpdate = (update: (current: string | null) => string | null) => void;

/** The effect body: sets the title and returns the cleanup, which clears the slot only while it still holds this consumer's title. */
export function leafEffect(set: LeafUpdate, title: string): () => void {
  set(() => title);
  return () => set((current) => clearLeaf(current, title));
}

// Layout effect, so the new leaf is in the same commit as the new route: `usePageTitle` never composes the old page's name with the
// new nav entry. It is a plain effect where there is no DOM (`useLayoutEffect` warns on the server).
const useLeafEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/** A page (or its breadcrumb) says what it is called; cleared when it unmounts. Text only: pass null while the name is not known. */
export function usePageTitleLeaf(title: string | null): void {
  const set = useContext(LeafSetter);
  useLeafEffect(() => {
    if (!set || !title) return;
    return leafEffect(set, title);
  }, [set, title]);
}
